/**
 * Search / sort / filter config for the workspace-wide Activations list, plus the label resolvers the
 * table and the card view share. Pure — no React, no network — so the transform is testable on its own
 * and both renderers derive their strings from one place.
 *
 * Two rules from the surface's product brief are encoded here rather than left to each renderer:
 *
 * 1. **A row stores `audienceId`, never an audience name.** {@link audienceLabel} resolves it against
 *    the register at render time and falls back to the raw id — so renaming an audience moves it in the
 *    table, the filter menu and the sort at once, and a dangling reference *looks* dangling.
 * 2. **Every number carries its denominator.** {@link syncedOfChanged} is the only place a run's
 *    changed → synced pair is turned into text, and it goes through `denominated()` from `run-view`.
 */
import dayjs from 'dayjs';

import type { FilterGroup, SortOption } from '@/_shared/components/list/ListToolbar';
import { timeAgo } from '@/_shared/lib/format';
import type { ListDeriveConfig, SortState } from '@/_shared/lib/list-controls';
import { warehouseTypeMeta } from '@/settings/components/warehouseAccount/warehouseTypes';

import type { ActivationRow, RawSqlAudience, RawSyncRun } from '../types';
import { denominated, isManualSchedule, PLAN_UNMEASURED } from './run-view';

/**
 * The workspace-relative path to one activation — pass through `wp()` before handing it to a router.
 *
 * The single definition of this route's shape, because an activation is addressed by a PAIR and the
 * first half is the easiest id in this domain to get wrong: `:sourceId` is the RudderStack rETL
 * source, which a list row calls `audienceId` (it is `sync.sourceId`, see `use-activations`) and
 * which is NOT the Lookout audience id that the Activate wizard holds. Two call sites reach for this
 * route from opposite directions — the list, from a row it already has, and the Activate sheet, from
 * what `enableActivation` just returned — so the naming mismatch is live in the codebase rather than
 * hypothetical. One builder keeps a route change from silently stranding one of them.
 */
export const activationPath = (sourceId: string, syncId: string): string => `/activations/${sourceId}/${syncId}`;

/**
 * The workspace-relative path to the Lookout audience behind an activation — the way BACK out of this
 * domain. Pass through `wp()` before handing it to a router.
 *
 * **The argument is the LOOKOUT audience id, and it is not any of the ids a row carries.** It comes
 * from `listActivationMeta`'s `audienceId` — the `audience_activations.audience_id` column — and it is
 * the only one of the three "audience" ids in this domain that addresses `/audiences/:id`. The other
 * two both fail silently: a row's `audienceId` is the rETL source (see {@link activationPath}), and
 * `destinationAudienceId` is Meta's or TikTok's. Wrong id here still routes, still renders a page, and
 * simply reports the audience as missing — which is why this is a named builder rather than a template
 * string at the call site.
 *
 * Only syncs Lookout itself minted or has since adopted have a meta row at all, so callers must treat
 * the absence of an id as ordinary and render no link — never a path built from a fallback.
 */
export const audiencePath = (lookoutAudienceId: string): string => `/audiences/${lookoutAudienceId}`;

/**
 * The workspace-relative path to the activations list, PRE-FILTERED to one audience — the way IN to
 * this domain from an audience page. Pass through `wp()` before handing it to a router.
 *
 * **The argument is the rETL SOURCE id** (`audiences.rs_source_id`), the same id {@link activationPath}
 * takes first and the same one a row calls `audienceId` — NOT the Lookout audience id that
 * {@link audiencePath} takes. That is not a wart to fix here: the value has to match what the list
 * actually filters on (`row.audienceId === value`, and the facet's options are built from
 * `RawSqlAudience.id`), so a Lookout id would be dropped as an unknown option and silently show the
 * unfiltered list.
 *
 * This deep-links into state the list ALREADY owns rather than adding a mode to it. `useListControls`
 * keeps filters in the URL as `f_<key>` precisely so they are shareable, and it drops a value outside
 * the facet's option set — so an audience whose syncs have all been deleted degrades to the full list
 * rather than to an empty one claiming a filter that no longer means anything. The coupling to the
 * `f_` convention and to the `audience` filter key is the price of that, and it is covered by a test
 * in `ActivationsList.test.tsx` that drives this exact path through the real hook.
 */
export const audienceActivationsPath = (rsSourceId: string): string =>
  `/activations?f_audience=${encodeURIComponent(rsSourceId)}`;

export type ActivationSortKey = 'updated' | 'lastRun' | 'destination' | 'audience';
export type ActivationFilterKey = 'audience' | 'status' | 'platform' | 'schedule';

/**
 * Whether the underlying rETL pipeline is switched ON — the fact the Status filter asks about and the
 * pilot light draws.
 *
 * Two-valued as a STATE — not because there is a single two-valued switch behind it. There is not.
 * `ActivationRow.enabled` is the AND of three independent columns on three tables (connection, source,
 * destination), written by four different controls in the RudderStack webapp with no cascade between
 * them. An earlier version of this comment claimed "two-valued because the switch is two-valued", and
 * the cost of that claim was a real defect: the UI read the connection bit alone, so a sync disabled at
 * its SOURCE rendered here as active while the webapp showed it disabled. `enabled` means all three are
 * on, so delivery is PERMITTED — not that anything has been delivered. A sync minted an hour ago and
 * never triggered is `enabled`, which is why the pilot light reads "Sync enabled" rather than the "Sync
 * active" it once claimed beside a Last run cell saying "No runs yet". `disabled` means at least one
 * switch is off and delivery has stopped. Which one is off is a question this list does not ask,
 * exactly as the webapp does not.
 *
 * **It is not run health.** How a run went is a separate and genuinely richer axis — `RawSyncRun.status`,
 * badged by `SyncStatusBadge`, read on Last run and Past runs. The two are independent: a disabled
 * pipeline can have a last run that succeeded, and an enabled one a last run that failed. That
 * independence is why a green dot and a green `Completed` badge can share a row without contradicting
 * each other — the dot says the switch is on, the badge says the last run worked. See the
 * `STATUS_DOT_CLASSES` note in `ActivationStrip`, which argues that collision from the other side.
 *
 * **Do not widen this union.** The values that look like candidates — `Setting up`, erroring, degraded —
 * belong to the run/provisioning axis, not to the switch. That the webapp knows `Setting up` only on the
 * sync *detail* page, from a per-sync `GET /sync-status` call, is the tell rather than the obstacle: it
 * is answering a different question. Widening here would turn the pilot light from a switch indicator
 * into a health indicator, which is the job the badge already holds — and would put the same fact in two
 * places at two altitudes, which is what retiring the Status column set out to stop.
 */
export type ActivationStatus = 'enabled' | 'disabled';

/** Automatic vs manual — the cadence question the Schedule filter asks. */
export type ScheduleKind = 'automatic' | 'manual';

// ─── Label resolvers (shared by the table cells, the cards, search and sort) ──

/**
 * The tail `mintActivationDestinationName` welds on: `-` followed by a `crypto.randomUUID()`, which the
 * procedure narrows to `z.uuid()` before the mint ever runs (`AudienceActivationSheet.tsx` →
 * `audience.ts`). Anchored to the end, because the account half of the name is arbitrary text.
 */
const MINTED_NAME_SUFFIX = /-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a destination name is one Lookout minted rather than one a person typed.
 *
 * Read off the STRING, not off the `audience_activations` join, and the difference is not academic: an
 * activation minted before that table existed has no meta row and would pass as webapp-built, which is
 * exactly the row observed in a browser still wearing a uuid after the join-based version of this fix.
 * The shape is one we generate ourselves and validate as a uuid on the way in, so it identifies every
 * minted name including the legacy ones — no load-order race, and nothing new on the wire.
 *
 * It also gets the rename case right, which the join could not: a minted destination someone later
 * renamed in the RudderStack webapp stops matching, and their name is shown. A human name that happens
 * to end in a uuid is the only false positive, and it costs a sub-line that falls back to the platform.
 */
export function isMintedDestinationName(name: string): boolean {
  return MINTED_NAME_SUFFIX.test(name);
}

/**
 * The destination's name — but ONLY when a human chose it. `null` otherwise, and the `null` is the
 * point of the function.
 *
 * This line used to render `destination.name` unconditionally, on the doctrine that "the account is
 * the thing a marketer recognises". The doctrine is right; the field was not. Every activation Lookout
 * creates mints its own destination named `{accountName}-{uuid}` — `mintActivationDestinationName`,
 * whose own comment reads "no destination-name UX" — so what shipped on exactly the rows this product
 * makes was a recognisable account name welded to 36 characters of entropy, in the muted sub-line of
 * the column the row is identified by. Nobody named it and nobody can rename it: it is a platform
 * handle that leaked into the reading surface.
 *
 * So a minted name resolves to `null` and callers fall back to the platform, which is what this line
 * said before the destination audience took the title. A webapp-built destination keeps its name,
 * because there a person typed it and it means something.
 */
export function destinationDisplayName(row: ActivationRow): string | null {
  const name = row.destination?.name;
  if (name === undefined || isMintedDestinationName(name)) return null;
  return name;
}

/** The platform behind the account, e.g. `Iterable Audience`. Still the Platform filter facet. */
export function platformLabel(row: ActivationRow): string {
  return row.destination?.displayName ?? 'Unknown platform';
}

/**
 * What the row is TITLED by: the audience on the far side — the thing the marketer went to Meta and
 * created, and the only field that differs between two syncs pointing at the same ad account.
 *
 * A three-rung ladder, and it is load-bearing rather than defensive. `destinationAudienceName` cannot
 * be made non-nullable: `listSqlAudiences` returns every sql-audience source in the workspace, most of
 * them built in the RudderStack webapp, and the backend deliberately refuses to adopt a source no
 * Lookout audience owns. So the majority of rows on a real workspace reach rung two or three.
 *
 * Every rung is TRUE. The far-side id is on the wire for every sync, so rung two states what the sync
 * actually points at; rung three is the destination it points into. What the ladder never does is
 * invent a name — the same doctrine as {@link audienceLabel}, and the design's own rule.
 *
 * Rung three is unreachable for a Lookout-minted row and that is structural, not luck: a meta row
 * always carries a `destinationAudienceId`, so anything minted stops at rung two. It is therefore only
 * webapp-built destinations that can be titled by their own name here, which is the one case where
 * that name was typed by a person.
 */
/**
 * Whether a sync is actually delivering: its own switch, its source's, and its destination's, ANDed.
 *
 * A missing bit reads as ON, matching the platform's column defaults — only an explicit `false` is a
 * disable, so an absent field must never manufacture one. The rule lives here because it was written
 * out twice (the list and the detail page) and had already drifted once, shipping a sync that was
 * disabled at its SOURCE as active.
 *
 * Note this is the PLATFORM's state, which Lookout only reads. Lookout cannot enable or disable a
 * pipeline; a user who wants a sync to stop shipping deletes the activation.
 */
export function isDeliveryEnabled(bits: {
  sync?: boolean | undefined;
  source?: boolean | undefined;
  destination?: boolean | undefined;
}): boolean {
  return (bits.sync ?? true) && (bits.source ?? true) && (bits.destination ?? true);
}

export function destinationAudienceLabel(row: ActivationRow): string {
  return (
    row.destinationAudienceName ?? row.destinationAudienceId ?? destinationDisplayName(row) ?? 'Unknown destination'
  );
}

/**
 * Whether {@link destinationAudienceLabel} returned a NAME (rung one) rather than an identifier.
 *
 * Renderers use this to style the other two rungs as identifiers — an id set in the same weight as a
 * name reads as a product that named something `23847…`, which is worse than admitting it has no name
 * for it yet.
 */
export function hasResolvedDestinationName(row: ActivationRow): boolean {
  return row.destinationAudienceName !== null;
}

/**
 * The line UNDER the title: the destination where a person named it, the platform otherwise.
 *
 * Two ways to reach the platform. A minted name is not shown at all ({@link destinationDisplayName}),
 * which is the common case and the one this list is mostly made of. And at the ladder's bottom rung the
 * title already IS the destination name — repeating it verbatim underneath states one fact twice and,
 * worse, makes a row that knows nothing about its far side look as though it knows two things about it.
 *
 * Not folded into the renderers because both of them need it and they must not drift.
 */
export function destinationSubLabel(row: ActivationRow): string {
  const name = destinationDisplayName(row);
  if (name === null || destinationAudienceLabel(row) === name) return platformLabel(row);
  return name;
}

/** The audience's name, resolved live; the raw id when the reference is broken. */
export function audienceLabel(row: ActivationRow, audienceNames: ReadonlyMap<string, string>): string {
  return audienceNames.get(row.audienceId) ?? row.audienceId;
}

/**
 * Display name of the row's warehouse, or `null` when the source carries no type (demo / older sources).
 * Wraps `warehouseTypeMeta` so aliases and casing normalise (`deltalake` → "Databricks").
 *
 * Truthiness, not `=== undefined`: `sourceDefinitionName` is `z.string().optional()`, so `''` is a valid
 * parse, not a missing field — and `warehouseTypeMeta('')` would return the "Warehouse" fallback, which
 * would then match every typeless row on the query "warehouse".
 */
export function warehouseTypeLabel(row: Pick<ActivationRow, 'warehouseType'>): string | null {
  return row.warehouseType ? warehouseTypeMeta(row.warehouseType).label : null;
}

/**
 * The accessible name of the control that opens a row — the table's row-activator button and the card
 * grid's stretched link, which must not name the same object two different ways.
 *
 * It states the whole relation, because that is what the row now shows: since `ActivationStrip` took
 * over as the identifier, "Open runs for Trial — no card (Q3)" named only the right-hand half of a cell
 * that draws both. Spelled as words rather than an arrow — a `→` inside an `aria-label` is announced as
 * "right arrow", or as nothing at all, depending on the reader.
 *
 * `audienceName` is passed in already resolved: the register lives in page state, and this module's
 * rule is that a row never carries an audience name of its own.
 *
 * **It also states a disabled sync, and it has to.** `ActivationStrip` draws the pilot-light dot and
 * says the state in its own `sr-only` connective — but this string becomes an `aria-label` on the
 * table's row-activator `<button>` and on the card's stretched link, and an `aria-label` REPLACES the
 * element's contents for a screen reader. So neither the dot nor the strip's connective is ever
 * announced on those two surfaces; whatever is not in here does not reach the reader at all. The
 * strip's own `sr-only` covers only the surfaces that do not wrap it (the detail header, the wizard).
 * Both are needed, neither alone is sufficient, and dropping either one is silent.
 *
 * Only the exception is stated. An active sync appends nothing, for the same reason no `Active` badge
 * was ever rendered: this name is read aloud on every row of the list, and a suffix that is true of
 * nearly all of them is noise in the one place a reader cannot skim past.
 */
export function activationRowLabel(row: ActivationRow, audienceName: string): string {
  const warehouse = warehouseTypeLabel(row);
  const origin = warehouse === null ? audienceName : `${audienceName} from ${warehouse}`;
  const base = `Open runs for ${origin} into ${destinationAudienceLabel(row)}`;
  return activationStatus(row) === 'disabled' ? `${base}, disabled` : base;
}

export function activationStatus(row: ActivationRow): ActivationStatus {
  return row.enabled ? 'enabled' : 'disabled';
}

export function scheduleKind(row: ActivationRow): ScheduleKind {
  return isManualSchedule(row.schedule) ? 'manual' : 'automatic';
}

// ─── Time ────────────────────────────────────────────────────────────────────

/** Milliseconds for sorting; an absent or unparseable timestamp is 0, so it lands at one end. */
function epochMs(iso: string | undefined): number {
  if (!iso) return 0;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

/**
 * `timeAgo` with the one guard it needs on this surface: `dayjs(x).fromNow()` renders an unparseable
 * input as the literal string "Invalid Date", and every timestamp here comes off a platform whose
 * payload shape `types.ts` documents as untrusted. `null` rather than a plausible-looking placeholder —
 * the caller decides what an unknown time looks like in its own slot.
 */
export function relativeTime(iso: string | undefined): string | null {
  if (!iso) return null;
  return dayjs(iso).isValid() ? timeAgo(iso) : null;
}

/**
 * `5 of 5 changed · 100.0%` — destination-successful over source-changed, the two-sided delta
 * compressed to one line. Built through `denominated()`, so the count never ships as a bare number:
 * it carries either its denominator or, when there is none to state, the words that say so; the
 * percentage drops out only when there is nothing to divide by.
 *
 * `of` is null when `changed.total` is the `-1` count-query-timeout sentinel — there is no plan size
 * to state, so this reads `PLAN_UNMEASURED` rather than fabricating a denominator or wearing the
 * NUMERATOR's count in the DENOMINATOR's unit (`unit` here describes `changed`, not `successful` —
 * dropping `of` while keeping `unit` would say "changed" of a count that is `successful.total`).
 * This cell sits in the workspace-wide Activations list, next to the run-detail page's
 * `RunDenominated` rendering of the very same run, so both import the one shared constant rather
 * than risking two near-identical phrases drifting apart.
 */
export function syncedOfChanged(run: RawSyncRun): string {
  const { value, of, unit, percent } = denominated(
    run.metrics?.successful?.total ?? 0,
    run.metrics?.changed?.total ?? 0,
    'changed',
  );
  if (of === null) return `${value} · ${PLAN_UNMEASURED}`;
  const pair = `${value} of ${of} ${unit}`;
  return percent === null ? pair : `${pair} · ${percent}`;
}

// ─── Toolbar options ─────────────────────────────────────────────────────────

export const ACTIVATION_SORT_KEYS: readonly ActivationSortKey[] = ['updated', 'lastRun', 'destination', 'audience'];

export const ACTIVATION_DEFAULT_SORT: SortState<ActivationSortKey> = { key: 'updated', dir: 'desc' };

/**
 * Typed to the sort KEYS, not the toolbar's loose `value: string`: an option added without a matching
 * comparator would otherwise compile and then throw inside `filterSortItems` the first time a user
 * picked it.
 */
export const ACTIVATION_SORT_OPTIONS: readonly (SortOption & { value: ActivationSortKey })[] = [
  { value: 'updated', label: 'Last updated' },
  { value: 'lastRun', label: 'Last run' },
  { value: 'destination', label: 'Destination audience' },
  { value: 'audience', label: 'Audience' },
  // No `account` option. It ordered rows by `destination.name`, which is a minted handle on every row
  // Lookout creates and is no longer rendered there — a sort over a field the reader cannot see, whose
  // order they cannot check, is a dead affordance. Grouping by platform stays reachable as a filter
  // facet, and `destination` orders by the name the row is actually titled with.
];

/** Stable filter-id set for `useListControls` (a module const, so the caller passes no fresh array). */
export const ACTIVATION_FILTER_KEYS: readonly ActivationFilterKey[] = ['audience', 'status', 'platform', 'schedule'];

const STATUS_OPTIONS = [
  // Key and label are the same word on purpose. The facet selects rows whose three switches are on,
  // which says nothing about whether one has ever run, so `active` was wrong in both halves — and with
  // the feature not yet in anyone's hands there was no bookmarked `?f_status=active` to preserve, which
  // is the only reason a serialised key is ever worth diverging from its label. Should a stale one turn
  // up, the toolbar already drops an out-of-set filter value from the URL rather than rendering a
  // phantom chip, so it degrades to "no filter" rather than to an error.
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
] as const;

const SCHEDULE_OPTIONS = [
  { value: 'automatic', label: 'Automatic' },
  { value: 'manual', label: 'Manual' },
] as const;

export interface ActivationFilters {
  /** Grouped, single-select-per-group filters for `ListToolbar`. */
  groups: FilterGroup[];
  /** The same options as bare value sets, so `useListControls` can reject a hand-edited URL param. */
  values: Partial<Record<ActivationFilterKey, readonly string[]>>;
}

/**
 * Audience and Platform options are derived from the rows on screen, not from every audience or every
 * supported platform: a filter option that can only ever return zero rows is a dead control. Status and
 * Schedule are closed sets, so they stay constant.
 */
export function buildActivationFilters(
  rows: readonly ActivationRow[],
  audiences: readonly RawSqlAudience[],
): ActivationFilters {
  const activated = new Set(rows.map((row) => row.audienceId));
  const audienceOptions = audiences
    .filter((audience) => activated.has(audience.id))
    .map((audience) => ({ value: audience.id, label: audience.name }));

  // definitionName → displayName: the value is what a row is compared against, the label is what a
  // reader recognises. A Map dedupes the many-rows-one-platform case in one pass.
  const platforms = new Map<string, string>();
  for (const row of rows) {
    if (row.destination) platforms.set(row.destination.definitionName, row.destination.displayName);
  }
  const platformOptions = [...platforms]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const groups: FilterGroup[] = [
    { key: 'audience', label: 'Audience', options: audienceOptions },
    { key: 'status', label: 'Status', options: STATUS_OPTIONS },
    { key: 'platform', label: 'Platform', options: platformOptions },
    { key: 'schedule', label: 'Schedule', options: SCHEDULE_OPTIONS },
  ];

  const values: Partial<Record<ActivationFilterKey, readonly string[]>> = {};
  for (const group of groups) values[group.key as ActivationFilterKey] = group.options.map((o) => o.value);

  return { groups, values };
}

// ─── Derive config ───────────────────────────────────────────────────────────

/**
 * Search covers the destination audience, the audience, the destination (where it has a name a person
 * chose), the platform and the warehouse type — the five things a reader types. Returned as separate
 * FIELDS, never joined:
 * a query has to land inside one of them, so "Lapsed Iterable" finds nothing rather than matching a
 * phrase that exists only across a field boundary.
 *
 * The design's config is `[destinationAudienceName, account]`, dropping audience because on an
 * audience-SCOPED list every row carries the same one — typing it narrows nothing while matching
 * everything. This list is workspace-wide, where the audience is precisely what varies (it is a column,
 * a facet and a sort key here), so this is the design's set plus the two fields its reasoning does not
 * apply to. Kept as separate fields, which is the half of the design's rule that does carry over.
 *
 * A factory because `audienceNames` is page state: the audience sort and the audience search field both
 * read the resolved name, which a rename moves.
 */
export function makeActivationDeriveConfig(
  audienceNames: ReadonlyMap<string, string>,
): ListDeriveConfig<ActivationRow, ActivationSortKey, ActivationFilterKey> {
  const audienceOf = (row: ActivationRow): string => audienceLabel(row, audienceNames);
  return {
    // A minted destination name contributes NO field here — matching a query against a uuid nobody can
    // see would surface a row for a reason its reader cannot reconstruct.
    searchText: (row) => {
      const warehouse = warehouseTypeLabel(row);
      return [
        destinationAudienceLabel(row),
        audienceOf(row),
        destinationDisplayName(row) ?? '',
        platformLabel(row),
        ...(warehouse === null ? [] : [warehouse]),
      ];
    },
    comparators: {
      // Parsed, not compared as strings: the platform mixes precision within one payload (a run's
      // `started_at` arrives second-precision while `updatedAt` carries milliseconds), and lexically
      // `"…:03Z"` sorts AFTER `"…:03.500Z"` because 'Z' > '.'. An offset like `+02:00` breaks it
      // outright. An absent timestamp sorts first ascending, i.e. last under the default `updated desc`.
      updated: (a, b) => epochMs(a.updatedAt) - epochMs(b.updatedAt),
      lastRun: (a, b) => epochMs(a.lastRun?.started_at) - epochMs(b.lastRun?.started_at),
      // Over the LABEL, not the raw name: sorting on a nullable field would pool every unresolved row
      // at one end, which is a sort by "has Lookout resolved this yet" — an implementation detail — and
      // not the alphabetical order the option promises.
      destination: (a, b) => destinationAudienceLabel(a).localeCompare(destinationAudienceLabel(b)),
      audience: (a, b) => audienceOf(a).localeCompare(audienceOf(b)),
    },
    filterPredicates: {
      audience: (row, value) => row.audienceId === value,
      status: (row, value) => activationStatus(row) === value,
      platform: (row, value) => row.destination?.definitionName === value,
      schedule: (row, value) => scheduleKind(row) === value,
    },
  };
}
